import {useMemo} from 'react';

import {useSelector} from 'react-redux';
import styled from 'styled-components';

import {selectOrderSuccessData, selectUserLastOrderScooberJob} from '~/shared/store/selectors';
import {Section} from '~/shared/components/OrderCompleteSections/parts';
import {defaultPaddingCss} from '~/shared/components/OrderCompleteSections/orderSummaryStyle';
import {flexCenter, flexColumn} from '~/shared/theme/FlexLayout';
import {media} from '~/shared/theme/media';
import {isValidTimeRangeString} from '~/shared/utils/timeRangeHelper';
import DeliveryTrackerView from '~/shared/components/DeliveryTrackerView/DeliveryTrackerView';
import useDeliveryTrackerCounter from '~/shared/components/DeliveryTrackerView/useDeliveryTrackerCounter';

import DeliveryTrackerStatus from '../DeliveryTrackerStatus';
import OrderSuccessMap from '../OrderSuccessMap';

import HeaderAndSubheader from './HeaderAndSubheader';
import OrderSuccessIcon from './order-success.svg?react';

const Spacer = styled.div`
  margin-top: 392px;
  display: flex;
  ${media.minLargeDesktop`
    margin-top: 442px;  
  `}
`;

const OrderHeaderSubheaderWrapper = styled(Section)`
  ${defaultPaddingCss}
  ${flexColumn}
  position: relative;
  top: 392px;
  margin-top: 0;
  ${media.minLargeDesktop`
    top: 442px;
  `}
`;

const OrderCompleteWrapper = styled.div`
  ${flexCenter}
  height: 95px;
  width: 95px;
  background-color: ${({theme}) => theme.colors.surface};
  border-radius: 50%;
`;

const OrderComplete = styled.div<{isOrderCompleted: boolean}>`
  ${flexCenter}
  height: 85px;
  width: 85px;
  background-color: ${({theme, isOrderCompleted}) => (isOrderCompleted ? theme.colors.supportPositive2 : theme.colors.darkBackground)};
  border-radius: 50%;
`;

const StyledDeliveryTrackerView = styled(DeliveryTrackerView)`
  position: absolute;
  bottom: -${47.5 - 16}px;
  left: 0;
  right: 0;
`;

export enum TrackerVariants {
  OD = 'od',
  DELIVERY_TRACKER = 'delivery_tracker'
}

type TrackerVariant = EnumValueType<typeof TrackerVariants>;

const InProgressTracker = ({trackerVariant}: {trackerVariant: TrackerVariant}) => {
  const orderSuccessData = useSelector(selectOrderSuccessData);
  const {status} = useSelector(selectUserLastOrderScooberJob) || {};

  const {
    minutesToRender: _minutesToRender,
    isToday,
    isOverTwoHours,
    isExceeded,
    isMoreThan10MinutesLeft,
    isOver20Minutes,
    isTrackerInitiated,
    totalEta,
  } = useDeliveryTrackerCounter({
    scheduledDateTime: orderSuccessData?.orderScheduledDateTime,
    scheduledDelta: orderSuccessData?.orderScheduledDelta,
    startingTimestamp: orderSuccessData?.orderPlacementDateTime,
    isDataReady: Boolean(orderSuccessData),
  });

  const arrivalOrderDateTimeText = useMemo(() => {
    if (!orderSuccessData) {
      return '';
    }

    if (isValidTimeRangeString(orderSuccessData.arrivalOrderDateTimeText)) {
      return orderSuccessData.arrivalOrderDateTimeText;
    }

    return orderSuccessData.arrivalOrderTime;
  }, [orderSuccessData]);

  if (!orderSuccessData) {
    return null;
  }

  const showDeliveryDummyTracker = trackerVariant === TrackerVariants.DELIVERY_TRACKER && isToday && !isExceeded;
  const showOdDummyTracker = trackerVariant === TrackerVariants.OD;
  const showDummyTracker = showOdDummyTracker || showDeliveryDummyTracker;
  const showDeliveryTrackerStatus = trackerVariant === TrackerVariants.DELIVERY_TRACKER;

  const minutesToRender = showDummyTracker ?
    _minutesToRender :
    orderSuccessData.arrivalOrderTime;

  if (!isTrackerInitiated) {
    return null;
  }

  return (
    <>
      <OrderSuccessMap
        restaurantLatitude={orderSuccessData.restaurant.location.latitude}
        restaurantLongitude={orderSuccessData.restaurant.location.longitude}
        userLatitude={orderSuccessData.address.location.latitude}
        userLongitude={orderSuccessData.address.location.longitude}
        showUntrackedShippingBadge={showOdDummyTracker}
        showDummyTracker={showDummyTracker || showOdDummyTracker}
      />
      <OrderHeaderSubheaderWrapper>
        {showDummyTracker && (
          <StyledDeliveryTrackerView
            minutesToRender={minutesToRender}
            totalEta={totalEta}
            isOverTwoHours={isOverTwoHours}
            showCircleProgress={!showOdDummyTracker || (showOdDummyTracker && isMoreThan10MinutesLeft)}
            noCounterReplacementComponent={(
              <OrderCompleteWrapper>
                <OrderComplete isOrderCompleted={isOver20Minutes}>
                  <OrderSuccessIcon/>
                </OrderComplete>
              </OrderCompleteWrapper>
            )}
          />
        )}
        <HeaderAndSubheader
          isToday={isToday}
          showDeliveryDummyTracker={showDeliveryDummyTracker}
          showOdDummyTracker={trackerVariant === TrackerVariants.OD}
          minutesToRender={minutesToRender}
          arrivalOrderDate={orderSuccessData.arrivalOrderDate}
          arrivalOrderTime={orderSuccessData.arrivalOrderTime}
          arrivalOrderDateTimeText={arrivalOrderDateTimeText}
          restaurantName={orderSuccessData.restaurant.restaurantName}
          isExceeded={isExceeded}
          isOverTwoHours={isOverTwoHours}
          isOver20Minutes={isOver20Minutes}
          isMoreThan10MinutesLeft={isMoreThan10MinutesLeft}
        />
      </OrderHeaderSubheaderWrapper>
      {showDeliveryTrackerStatus ? <DeliveryTrackerStatus status={status} mapDisplayed isShowDeliveryTracker /> : <Spacer />}
    </>
  );
};

export default InProgressTracker;
